/**
 * 表示モデル（§0.3.1・U151 / U157 / U158）。
 * 「1 行あたり塩基数」「ズーム」「座標ラベル刻み」を描画から切り離した純関数にする。
 */

import { Topology, normalizeBase } from '../sequence/interval';

/** 塩基文字が読める最小の 1 塩基幅（CSS px）。これを下回ればマップ主体に切り替える。 */
export const MIN_READABLE_BASE_WIDTH = 6;
/** 等幅フォントの字幅／字高のおおよその比 */
export const MONOSPACE_CHAR_ASPECT = 0.6;
/** 10 bp ブロック（§0.3.1） */
export const BLOCK_SIZE = 10;

export interface SequenceLayoutInput {
  /** 中央ペインの CSS 幅 */
  viewportWidth: number;
  /** 塩基のフォントサイズ（CSS px） */
  fontSize: number;
  /** 座標ラベル欄の幅（CSS px） */
  gutterWidth: number;
  /** 左右の余白（CSS px） */
  paddingX?: number;
  /** ブロック間の空き（塩基幅の倍数） */
  blockGapChars?: number;
  charAspect?: number;
  blockSize?: number;
  /** ブラウザズーム込みのデバイスピクセル比 */
  devicePixelRatio?: number;
}

export interface SequenceLayout {
  basesPerLine: number;
  blocksPerLine: number;
  blockSize: number;
  /** 1 塩基の CSS 幅 */
  baseWidth: number;
  /** ブロック間の空き（CSS px） */
  blockGap: number;
  /** 塩基が占める CSS 幅 */
  contentWidth: number;
  devicePixelRatio: number;
  /** Canvas の実ピクセル幅（DPR 補正済み・U156） */
  deviceWidth: number;
}

/**
 * ビューポート幅に合わせて 1 行の塩基数を決める（U157）。
 * 固定 60 字幅で余白を余らせない。10 bp ブロックは維持したままブロック数を増やす。
 */
export function computeSequenceLayout(input: SequenceLayoutInput): SequenceLayout {
  const blockSize = input.blockSize ?? BLOCK_SIZE;
  const charAspect = input.charAspect ?? MONOSPACE_CHAR_ASPECT;
  const paddingX = input.paddingX ?? 0;
  const devicePixelRatio = input.devicePixelRatio ?? 1;
  const baseWidth = Math.max(1, input.fontSize * charAspect);
  const blockGapChars = input.blockGapChars ?? 1;
  const blockGap = baseWidth * blockGapChars;

  const available = Math.max(0, input.viewportWidth - input.gutterWidth - paddingX * 2);
  const blockWidth = blockSize * baseWidth;
  const blocksPerLine = Math.max(1, Math.floor((available + blockGap) / (blockWidth + blockGap)));
  const basesPerLine = blocksPerLine * blockSize;
  const contentWidth = blocksPerLine * blockWidth + (blocksPerLine - 1) * blockGap;

  return {
    basesPerLine,
    blocksPerLine,
    blockSize,
    baseWidth,
    blockGap,
    contentWidth,
    devicePixelRatio,
    deviceWidth: Math.round(input.viewportWidth * devicePixelRatio),
  };
}

// ------------------------------------------------------------------ ズーム

export interface ViewRange {
  start: number;
  end: number;
}

export const MIN_VISIBLE_BASES = 20;

export function rangeLength(range: ViewRange): number {
  return Math.max(0, range.end - range.start);
}

export function basesPerPixel(range: ViewRange, viewportWidth: number): number {
  return viewportWidth > 0 ? rangeLength(range) / viewportWidth : 0;
}

/** 塩基文字として読める縮尺か（読めなければ注釈付きマップが主・§0.3.1） */
export function isSequenceReadable(
  range: ViewRange,
  viewportWidth: number,
  minBaseWidth: number = MIN_READABLE_BASE_WIDTH
): boolean {
  const bpp = basesPerPixel(range, viewportWidth);
  return bpp > 0 && 1 / bpp >= minBaseWidth;
}

/**
 * Ctrl+ホイール相当の連続ズーム（U151）。`anchorBase` を画面上に留めたまま拡大縮小する。
 * `factor` < 1 で拡大（表示範囲が狭くなる）。
 */
export function zoomRange(
  range: ViewRange,
  factor: number,
  anchorBase: number,
  genomeLength: number,
  minVisibleBases: number = MIN_VISIBLE_BASES
): ViewRange {
  const currentLength = rangeLength(range);
  if (currentLength <= 0 || genomeLength <= 0) return range;

  const maxLength = genomeLength;
  const minLength = Math.min(minVisibleBases, genomeLength);
  const nextLength = Math.min(maxLength, Math.max(minLength, currentLength * factor));

  const anchorRatio = currentLength > 0 ? (anchorBase - range.start) / currentLength : 0.5;
  let start = anchorBase - anchorRatio * nextLength;
  start = Math.min(Math.max(0, start), Math.max(0, genomeLength - nextLength));
  return { start, end: start + nextLength };
}

/** 横移動。環状は原点をまたいで回り込む。 */
export function panRange(
  range: ViewRange,
  deltaBases: number,
  genomeLength: number,
  topology: Topology = 'linear'
): ViewRange {
  const length = rangeLength(range);
  if (topology === 'circular') {
    const start = normalizeBase(range.start + deltaBases, genomeLength, 'circular');
    return { start, end: start + length };
  }
  const start = Math.min(Math.max(0, range.start + deltaBases), Math.max(0, genomeLength - length));
  return { start, end: start + length };
}

/** 分子全体を表示する範囲 */
export function fullRange(genomeLength: number): ViewRange {
  return { start: 0, end: genomeLength };
}

// -------------------------------------------------------------- 座標ラベル

const TICK_STEPS = [1, 2, 5];

/**
 * 目盛り刻み（U158）。1・2・5 × 10^n から、ラベルが `minPixelGap` 以上離れる最小の刻みを選ぶ。
 * chr1 全景では Mbp 刻み、局所では 10 bp 刻みになる。
 */
export function chooseTickStep(
  basesPerPixelValue: number,
  minPixelGap = 80,
  minStep = 1
): number {
  const requiredBases = Math.max(minStep, basesPerPixelValue * minPixelGap);
  let magnitude = 10 ** Math.floor(Math.log10(Math.max(1, requiredBases)));
  for (let guard = 0; guard < 24; guard += 1) {
    for (const step of TICK_STEPS) {
      const candidate = step * magnitude;
      if (candidate >= requiredBases) return candidate;
    }
    magnitude *= 10;
  }
  return requiredBases;
}

/** 折返し表示の座標ラベル刻み。1 行の塩基数の約数に寄せて潰れないようにする。 */
export function chooseLineLabelStep(basesPerLine: number, blockSize = BLOCK_SIZE): number {
  return Math.max(blockSize, basesPerLine);
}

/** 可視範囲に入る目盛り位置 */
export function tickPositions(range: ViewRange, step: number, genomeLength: number): number[] {
  if (step <= 0) return [];
  const ticks: number[] = [];
  const first = Math.ceil(range.start / step) * step;
  for (let position = first; position < range.end && position <= genomeLength; position += step) {
    ticks.push(position);
  }
  return ticks;
}

/**
 * 目盛りラベル（U158）。刻みが細かいのに Mb / kb へ丸めると、
 * chr1 の 1,000,000 付近で「1000 kb」が並んで隣の目盛りと区別できなくなる。
 * 刻み幅に合わせて単位と小数桁を決め、bp 刻みでは実座標をそのまま出す。
 */
export function formatAxisLabel(position: number, step: number): string {
  if (step >= 1_000_000) {
    return `${(position / 1_000_000).toFixed(unitDecimals(step, 1_000_000))} Mb`;
  }
  if (step >= 1_000) {
    return `${(position / 1_000).toFixed(unitDecimals(step, 1_000))} kb`;
  }
  return Math.round(position).toLocaleString('en-US');
}

function unitDecimals(step: number, unit: number): number {
  return Math.max(0, Math.ceil(-Math.log10(step / unit)));
}

/** 大きい座標を短く見せる（染色体級のラベル用） */
export function formatCompactBase(position: number): string {
  if (position >= 1_000_000) {
    const value = position / 1_000_000;
    return `${value >= 100 ? value.toFixed(0) : value.toFixed(value >= 10 ? 1 : 2)} Mb`;
  }
  if (position >= 1_000) {
    const value = position / 1_000;
    return `${value >= 100 ? value.toFixed(0) : value.toFixed(1)} kb`;
  }
  return `${position}`;
}
